// SamplerSignaturesSection.js
// This component handles the Sampler Signatures section of the Wells tab

import React, { useState } from "react";
import Select from "react-select";
// Components
import { ConfirmationModal } from "../../../common/ConfirmationModal";
import { SignatureModal } from "../../../../common/SignatureModal";
import styles from "./SamplerSignaturesSection.module.css";
import commonStyles from "../WellsTab.module.css";

// Employee select Input for the SignatureModal
const SamplerEmployeeSelect = ({
  selectedSignature,
  onEmployeeChange,
  allEmployees,
  samplingEventMode,
}) => {
  const handleEmployeeChange = (selected) => {
    if (!onEmployeeChange) {
      return;
    }

    if (!selected) {
      onEmployeeChange(null);
    } else {
      onEmployeeChange({
        employeeId: selected.value,
        employeeName: selected.label,
      });
    }
  };

  return (
    <Select
      value={
        selectedSignature?.employeeId
          ? {
              value: selectedSignature.employeeId,
              label: selectedSignature.employeeName,
            }
          : null
      }
      onChange={handleEmployeeChange}
      options={allEmployees.map((emp) => ({
        value: emp.employee_id,
        label: emp.name,
      }))}
      isDisabled={samplingEventMode === "view"}
      placeholder="Select Employee"
      isClearable={true}
      isSearchable={true}
      className={styles.employeeSelect}
    />
  );
};

// Employee Signature Row Component
const SamplerSignatureRow = ({
  signature,
  samplingEventMode,
  editSignature,
  deleteSignature,
}) => {
  const handleEditSignature = () => {
    editSignature(signature);
  };

  const handleDeleteSignature = () => {
    deleteSignature(signature);
  };

  return (
    <div className={styles.signatureCard} key={signature.rowId}>
      <div className={styles.signatureCardHeader}>
        <label className={styles.signatureLabel}>Employee</label>
        <div className={styles.signatureValue}>
          {signature.employeeName || (
            <span className={styles.emptyValue}>No employee selected</span>
          )}
        </div>
      </div>

      <div
        className={styles.signatureDisplay}
        onClick={samplingEventMode !== "view" ? handleEditSignature : undefined}
      >
        {signature.signature ? (
          <img
            src={signature.signature}
            alt="Employee signature"
            className={styles.signatureImage}
          />
        ) : (
          <div className={styles.emptySignature}>
            <span>Click to sign/edit</span>
          </div>
        )}
      </div>

      <div className={styles.signatureCardActions}>
        <button
          className={styles.signButton}
          onClick={handleEditSignature}
          disabled={samplingEventMode === "view"}
        >
          Edit
        </button>
        <button
          className={styles.deleteButton}
          onClick={handleDeleteSignature}
          disabled={samplingEventMode === "view"}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

// Main Sampler Signatures Section Component
export const SamplerSignaturesSection = ({
  well,
  allEmployees,
  samplingEventMode,
  ui,
  wellsActions,
  emitPatch,
}) => {
  // Signature modal via store
  const isSignatureModalOpen = ui.isSamplerSignatureModalOpen;
  // Centralized delete confirm via store
  const isConfirmModalOpen = ui.isSamplerSignatureConfirmationModalOpen;

  // Local state for employee updates
  const [selectedSignature, setSelectedSignature] = useState(null);

  // Revision counter to force re-renders when class instance properties change
  const [, setRevision] = useState(0);

  // Local state for signature to delete
  const [signatureToDelete, setSignatureToDelete] = useState(null);

  // Handle add sampler signature
  const handleAddSamplerSignature = () => {
    const newSignature = wellsActions.addSamplerSignature(well.id, emitPatch);
    if (newSignature) {
      wellsActions.openSamplerSignatureModal();
      setSelectedSignature(newSignature);
    }
  };

  // Handle open signature modal
  const handleEditSignature = (signature) => {
    // Set the local state for the employee
    setSelectedSignature(signature);
    wellsActions.openSamplerSignatureModal();
  };

  // Handle employee selection change in the modal
  const handleEmployeeSelectionChange = (employeeData) => {
    if (!selectedSignature?.setSelectedEmployee) return;

    // Update employee on the signature class instance
    selectedSignature.setSelectedEmployee(employeeData);

    // Force re-render by incrementing revision counter
    // This keeps the class instance intact while triggering React update
    setRevision((r) => r + 1);
  };

  // Handle save signature
  const handleSaveSignature = (signatureData) => {
    if (!selectedSignature) return;

    wellsActions.updateSamplerSignature(
      well.id,
      selectedSignature.rowId,
      [
        {
          field: "employeeId",
          // Pass the full employee object as setSelectedEmployee expects { employeeId, employeeName }
          value: {
            employeeId: selectedSignature.employeeId,
            employeeName: selectedSignature.employeeName,
          },
        },
        {
          field: "signature",
          // Use the signatureData from the canvas, not the existing signature
          value: signatureData,
        },
      ],
      emitPatch
    );
    wellsActions.closeSamplerSignatureModal();
    setSelectedSignature(null);
  };

  const handleOpenDeleteConfirm = (signature) => {
    wellsActions.openSamplerSignatureConfirmationModal();
    setSignatureToDelete(signature);
  };

  const handleOnConfirmDelete = () => {
    if (signatureToDelete) {
      wellsActions.removeSamplerSignature(
        well.id,
        signatureToDelete.rowId,
        emitPatch
      );
      wellsActions.closeSamplerSignatureConfirmationModal();
      setSignatureToDelete(null);
    }
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    wellsActions.closeSamplerSignatureConfirmationModal();
    setSignatureToDelete(null);
  };

  // Render sampler signature cards
  const renderSignatureCards = () => {
    if (!well.employeeSignatures || well.employeeSignatures.length === 0) {
      return (
        <div className={styles.noSignatures}>
          No sampler signatures recorded. Click "Add Signature" to add one.
        </div>
      );
    }

    return (
      <div className={styles.signatureCardList}>
        {well.employeeSignatures.map((signature) => (
          <SamplerSignatureRow
            key={signature.rowId}
            signature={signature}
            samplingEventMode={samplingEventMode}
            editSignature={handleEditSignature}
            deleteSignature={handleOpenDeleteConfirm}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={styles.samplerSignaturesSection}>
      <div className={styles.sectionHeader}>
        <h4>6. Sampler Signatures</h4>
        <button
          className={styles.addSignatureButton}
          onClick={handleAddSamplerSignature}
          disabled={samplingEventMode === "view"}
        >
          Add Signature
        </button>
      </div>

      {/* Render signature cards */}
      {renderSignatureCards()}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => {
          wellsActions.closeSamplerSignatureModal();
        }}
        signee={selectedSignature?.rowId}
        existingSignature={selectedSignature?.signature}
        onSave={handleSaveSignature}
        onClear={() => {}}
        components={[
          <SamplerEmployeeSelect
            key="employeeSelect"
            selectedSignature={selectedSignature || {}}
            onEmployeeChange={handleEmployeeSelectionChange}
            allEmployees={allEmployees || []}
            samplingEventMode={samplingEventMode}
          />,
        ]}
      />

      {ui.isSamplerSignatureConfirmationModalOpen && (
        <ConfirmationModal
          title="Delete Signature"
          message="Are you sure you want to delete this signature? This action cannot be undone."
          onCancel={handleCancelDelete}
          onConfirm={handleOnConfirmDelete}
          styles={{
            confirmationModalOverlay: commonStyles.modalOverlay,
            confirmationModalContent: commonStyles.modalContent,
            confirmationModalHeader: commonStyles.modalHeader,
            confirmationModalMessage: commonStyles.modalMessage,
            confirmationModalButtons: commonStyles.modalButtons,
            confirmCancel: commonStyles.cancelButton,
            confirmButton: commonStyles.confirmButton,
          }}
          confirmButtonClass="confirmButton"
        />
      )}
    </div>
  );
};
